import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import geniebotLogo from '@/assets/geniebot-robot-logo-sm.webp';

type AppState = 'idle' | 'loading' | 'success' | 'error';

interface GenieBotPulseProps {
  state?: AppState;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const stateConfig = {
  idle: {
    color: 'from-primary/60 to-primary/30',
    pulseColor: 'bg-primary/20',
    glowColor: 'shadow-primary/20',
    animation: { scale: [1, 1.05, 1], opacity: [0.6, 0.8, 0.6] },
    duration: 3,
  },
  loading: {
    color: 'from-blue-500/60 to-cyan-500/30',
    pulseColor: 'bg-blue-500/20',
    glowColor: 'shadow-blue-500/30',
    animation: { scale: [1, 1.2, 1], opacity: [0.4, 1, 0.4] },
    duration: 0.8,
  },
  success: {
    color: 'from-green-500/60 to-emerald-500/30',
    pulseColor: 'bg-green-500/20',
    glowColor: 'shadow-green-500/30',
    animation: { scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] },
    duration: 0.5,
  },
  error: {
    color: 'from-red-500/60 to-orange-500/30',
    pulseColor: 'bg-red-500/20',
    glowColor: 'shadow-red-500/30',
    animation: { scale: [1, 1.1, 1, 1.1, 1], opacity: [0.6, 1, 0.6, 1, 0.6] },
    duration: 0.4,
  },
};

const sizeConfig = {
  sm: { container: 'h-6 w-6', icon: 'h-4 w-4' },
  md: { container: 'h-8 w-8', icon: 'h-6 w-6' },
  lg: { container: 'h-10 w-10', icon: 'h-8 w-8' },
};

const stateLabel: Record<AppState, string> = {
  idle: 'GenieBot status: ready',
  loading: 'GenieBot status: working',
  success: 'GenieBot status: completed',
  error: 'GenieBot status: error',
};

export function GenieBotPulse({ state = 'idle', size = 'md', className }: GenieBotPulseProps) {
  const config = stateConfig[state];
  const sizeStyles = sizeConfig[size];

  return (
    <div className={cn('relative', className)} role="status">
      <span className="sr-only">{stateLabel[state]}</span>
      {/* Outer pulse ring */}
      <motion.div
        className={cn(
          'absolute inset-0 rounded-full',
          config.pulseColor
        )}
        animate={{
          scale: state === 'loading' ? [1, 2, 2] : [1, 1.5, 1],
          opacity: state === 'loading' ? [0.5, 0.2, 0] : [0.3, 0.1, 0.3],
        }}
        transition={{
          duration: config.duration,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Middle pulse ring (loading only) */}
      <AnimatePresence>
        {state === 'loading' && (
          <motion.div
            initial={{ scale: 1, opacity: 0 }}
            animate={{ scale: [1, 1.8], opacity: [0.4, 0] }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: 0.3,
            }}
            className={cn(
              'absolute inset-0 rounded-full',
              config.pulseColor
            )}
          />
        )}
      </AnimatePresence>

      {/* Main orb */}
      <motion.div
        className={cn(
          'relative rounded-full flex items-center justify-center',
          'bg-gradient-to-br',
          config.color,
          config.glowColor,
          'shadow-lg',
          sizeStyles.container
        )}
        animate={config.animation}
        transition={{
          duration: config.duration,
          repeat: Infinity,
          ease: state === 'error' ? 'easeInOut' : 'easeInOut',
        }}
      >
        <motion.div
          animate={state === 'loading' ? { rotate: 360 } : { rotate: 0 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'linear',
          }}
          className="overflow-hidden rounded-full"
        >
          <img 
            src={geniebotLogo} 
            alt="" 
            className={cn('object-cover rounded-full', sizeStyles.icon)} 
          />
        </motion.div>
      </motion.div>

      {/* Success burst effect */}
      <AnimatePresence>
        {state === 'success' && (
          <>
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  scale: 0, 
                  opacity: 1,
                  x: 0,
                  y: 0,
                }}
                animate={{ 
                  scale: [0, 1],
                  opacity: [1, 0],
                  x: Math.cos((i * 60) * (Math.PI / 180)) * 20,
                  y: Math.sin((i * 60) * (Math.PI / 180)) * 20,
                }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6,
                  delay: i * 0.05,
                }}
                className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-400"
              />
            ))}
          </>
        )}
      </AnimatePresence>

      {/* Error shake particles */}
      <AnimatePresence>
        {state === 'error' && (
          <>
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 0 }}
                animate={{ 
                  opacity: [0, 1, 0],
                  y: [-5, -15],
                  x: [(i - 1) * 8, (i - 1) * 12],
                }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.5,
                  delay: i * 0.1,
                }}
                className="absolute left-1/2 top-0 h-1 w-1 rounded-full bg-red-400"
              />
            ))}
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Simple hook placeholder for app state management
export const useAppPulseState = () => {
  return {
    state: 'idle' as AppState,
    setLoading: () => {},
    setSuccess: () => {},
    setError: () => {},
    reset: () => {},
  };
};
